import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';

interface NavItem {
  key: string;
  label: string;
  icon: string;
}

export class NavigationBar extends Phaser.GameObjects.Container {
  private navItems: Array<{ key: string; bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text; icon: Phaser.GameObjects.Text }> = [];
  private activeKey: string;
  private onNavigate: (key: string) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    activeKey: string,
    onNavigate: (key: string) => void
  ) {
    super(scene, x, y);

    this.activeKey = activeKey;
    this.onNavigate = onNavigate;

    this.createBar();
    this.createNavItems();
    scene.add.existing(this);
  }

  private createBar(): void {
    const bg = this.scene.add.rectangle(0, 0, 1280, 70, parseInt(DESIGN.colors.surface.replace('#', '0x'), 16), 0.95);
    bg.setOrigin(0, 0.5);
    this.add(bg);

    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.05);
    shadow.fillRect(0, 0, 1280, 4);
    this.add(shadow);
  }

  private createNavItems(): void {
    const items: NavItem[] = [
      { key: 'play', label: 'Arcade', icon: '🕹️' },
      { key: 'collection', label: 'Collection', icon: '📚' },
      { key: 'analytics', label: 'Analytics', icon: '📊' },
      { key: 'debug', label: 'Debug', icon: '🐛' },
      { key: 'settings', label: 'Settings', icon: '⚙️' },
    ];

    const startX = 220;
    const gap = 190;

    items.forEach((item, index) => {
      const isActive = this.activeKey === item.key;
      const x = startX + index * gap;

      const btn = this.scene.add.container(x, 0);

      const width = 160;
      const height = 46;
      const bgColor = isActive
        ? parseInt(DESIGN.colors.primary.replace('#', '0x'), 16)
        : parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16);
      const textColor = isActive ? '#ffffff' : DESIGN.colors.onSurface;

      const bg = this.scene.add.rectangle(0, 0, width, height, bgColor).setInteractive({ useHandCursor: true });
      bg.setOrigin(0.5);
      btn.add(bg);

      const icon = this.scene.add.text(-50, 0, item.icon, { fontSize: '20px' }).setOrigin(0.5);
      btn.add(icon);

      const label = this.scene.add.text(12, 0, item.label, {
        fontFamily: DESIGN.fonts.body,
        fontSize: '16px',
        color: textColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);
      btn.add(label);

      bg.on('pointerdown', () => {
        if (this.activeKey !== item.key) {
          this.onNavigate(item.key);
        }
      });

      this.add(btn);
      this.navItems.push({ key: item.key, bg, label, icon });
    });

    // Logo on the left
    const logo = this.scene.add.text(40, 0, 'Toybox Arcade', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '26px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add(logo);

    // XP badge on the right
    const badge = this.scene.add.rectangle(1180, 0, 80, 34, parseInt(DESIGN.colors.secondaryContainer.replace('#', '0x'), 16), 0.8);
    badge.setOrigin(0.5);
    this.add(badge);
    this.add(this.scene.add.text(1180, 0, '0 XP', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.secondary,
      fontStyle: 'bold',
    }).setOrigin(0.5));
  }

  setActiveKey(key: string): void {
    this.activeKey = key;
    this.navItems.forEach((item) => {
      const isActive = item.key === key;
      item.bg.setFillStyle(isActive
        ? parseInt(DESIGN.colors.primary.replace('#', '0x'), 16)
        : parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16)
      );
      item.label.setColor(isActive ? '#ffffff' : DESIGN.colors.onSurface);
    });
  }
}
